/**
 * InputBar component
 *
 * Text input for sending messages with send button and share menu.
 */

import { useState, useCallback, useRef, useEffect, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ShareMenu } from './ShareMenu';

interface InputBarProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  sessionUuid?: string;
  hasMessages?: boolean;
}

// Detect mobile: touch device OR narrow screen
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => {
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isNarrow = window.innerWidth <= 768;
      setIsMobile(hasTouch && isNarrow);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return isMobile;
}

export function InputBar({
  onSend,
  disabled = false,
  placeholder,
  sessionUuid,
  hasMessages = false,
}: InputBarProps) {
  const { t } = useTranslation();
  const finalPlaceholder = placeholder || t('chat.placeholder');
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = useIsMobile();

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (trimmed && !disabled) {
      onSend(trimmed);
      setValue('');
      // Re-focus input after send
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [value, disabled, onSend]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Mobile: Enter = new line, only send button sends
      // Desktop: Enter = send, Shift+Enter = new line
      if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend, isMobile]
  );

  return (
    <div className="input-area">
      <textarea
        ref={textareaRef}
        data-testid="chat-input"
        id="input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={finalPlaceholder}
        disabled={disabled}
        rows={1}
      />
      <div className="input-actions">
        {sessionUuid && hasMessages && (
          <ShareMenu sessionUuid={sessionUuid} disabled={disabled} />
        )}
        <button
          data-testid="send-button"
          className="send-btn"
          onClick={handleSend}
          disabled={disabled || !value.trim()}
        >
          ➤
        </button>
      </div>
    </div>
  );
}

export default InputBar;
